import {
  ChangeEventHandler,
  DetailedHTMLProps,
  FocusEventHandler,
  InputHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useFela } from 'react-fela';
import { FelaCSS, FelaCSSWithCustomSelectors } from '@bridebook/ui/src/components/fela/flowtypes';
import { ValidationError } from '@bridebook/toolbox';
import mergeStyles from '@bridebook/ui/src/fela-utils/merge-styles';
import Box from '../../fela/Box';
import componentStyles from './text-area.style';

type TextAreaNativeProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
  'style' | 'ref' | 'onChange' | 'onFocus' | 'onBlur' | 'onClick'
>;

type TextAreaVariant = 'default' | 'borderless';

interface IProps extends TextAreaNativeProps {
  label?: string;
  placeholder?: string;
  onChange?(value: string): void;
  onFocus?(value: string): void;
  onBlur?(value: string): void;
  onClick?: MouseEventHandler<ReactNode>;
  value?: string;
  style?: FelaCSS | FelaCSSWithCustomSelectors;
  innerWrapperStyle?: FelaCSS | FelaCSSWithCustomSelectors;
  rows?: number;
  fullHeight?: boolean;
  disabled?: boolean;
  controlled?: boolean;
  maxLength?: number;
  withAdjustableHeight?: boolean;
  name?: string;
  /**
   * Error message or validation error object. Passing `boolean` will prevent error
   * message from showing up, only affect input border color. Passing `string` or
   * `ValidationError` instance will show error message under the input.
   */
  error?: ValidationError | string | boolean | null;
  /**
   * Hides validation error message. This property is useful only when error property type is
   * typeof ValidationError. It allows to proceed error.prop with input.name comparison but
   * hides error message.
   */
  hideValidationErrorMsg?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  variant?: TextAreaVariant;
  /**
   * Shows character counter when true. Requires maxLength to be set.
   */
  showCharCounter?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, IProps>((props, ref) => {
  const {
    label,
    placeholder,
    onChange,
    disabled,
    required,
    onFocus,
    onBlur,
    onClick,
    value,
    style = {},
    innerWrapperStyle = {},
    fullHeight,
    rows = 5,
    controlled,
    maxLength,
    withAdjustableHeight,
    name,
    error,
    hideValidationErrorMsg,
    autoFocus,
    readOnly,
    variant = 'default',
    id,
    showCharCounter,
    ...rest
  } = props;

  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { css } = useFela();

  useImperativeHandle(ref, () => inputRef.current, []);

  useEffect(() => {
    if (!controlled) {
      setInputValue(value);
    }
  }, [value, controlled]);

  // Used in styling. Sets input border to error state.
  const hasError =
    error === true ||
    typeof error === 'string' ||
    (error instanceof ValidationError && error.prop === name);

  const showErrorMsg =
    typeof error === 'string' ||
    (error instanceof ValidationError && error.prop === name && !hideValidationErrorMsg);

  const styles = componentStyles({
    hasError,
    disabled,
    variant,
  });

  // Increases/decreases height of textarea as user types
  const adjustInputHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  };

  // Adjusts height of textarea on load - usable with prefilled text (for example when editing something)
  useEffect(() => {
    if (withAdjustableHeight) {
      adjustInputHeight();
    }
  }, [withAdjustableHeight]);

  const handleOnClick: MouseEventHandler<ReactNode> = useCallback(
    (e) => {
      onClick?.(e);
      inputRef.current?.focus();
    },
    [onClick],
  );

  const onInputChangeHandler: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const newValue = e.target.value;
    onChange?.(newValue);
    if (!controlled) {
      setInputValue(newValue);
    }
    withAdjustableHeight && adjustInputHeight();
  };

  const onInputFocusHandler: FocusEventHandler<HTMLTextAreaElement> = (e) => {
    const newValue = e.target.value;
    onFocus?.(newValue);
  };

  const onInputBlurHandler: FocusEventHandler<HTMLTextAreaElement> = (e) => {
    const newValue = e.target.value;
    onBlur?.(newValue);
  };

  // Focus on input when autoFocus is true and move the cursor to the end
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end
      inputRef.current.setSelectionRange(
        inputRef.current.value.length,
        inputRef.current.value.length,
      );
    }
  }, [autoFocus]);

  return (
    <Box>
      <Box style={mergeStyles([styles.wrapper, style])}>
        <Box w="100%" h={fullHeight ? '100%' : undefined}>
          <Box
            style={mergeStyles([styles.innerWrapper, innerWrapperStyle])}
            onClick={handleOnClick}
            setRef={wrapperRef}
            h={fullHeight ? '100%' : undefined}>
            <Box style={styles.label}>
              {label}
              {required && '*'}
            </Box>
            <textarea
              id={id}
              name={name}
              placeholder={placeholder}
              ref={inputRef}
              className={css(styles.input)}
              disabled={disabled}
              onChange={onInputChangeHandler}
              onFocus={onInputFocusHandler}
              onBlur={onInputBlurHandler}
              value={controlled ? value : inputValue}
              rows={rows}
              maxLength={maxLength}
              autoFocus={autoFocus}
              readOnly={readOnly}
              {...rest}
            />
          </Box>
        </Box>
      </Box>
      {showCharCounter && maxLength && (
        <Box style={styles.charCounter}>
          {(controlled ? value?.length : inputValue?.length) || 0} / {maxLength}
        </Box>
      )}
      {showErrorMsg && (
        <Box as="p" style={styles.error}>
          {typeof error === 'string' ? error : error.message}
        </Box>
      )}
    </Box>
  );
});

TextArea.displayName = 'TextArea';
